// CountrySidebar.jsx — fixed input focus loss bug
// Root cause: SidebarContent was defined INSIDE the component, causing React
// to treat it as a new component type on every render → unmount/remount → lost focus.
// Fix: inline the JSX directly; no nested component definitions.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, MapPin, X, Menu } from 'lucide-react';

function buildSlug(country, currency) {
  const countrySlug = country
    .toLowerCase()
    .replace(/[()&]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return `gold-price-in-${countrySlug}-${currency.toLowerCase()}`;
}

const REGIONS = [
  { name: 'Major Currencies', emoji: '🌐', currencies: ['USD','EUR','GBP','JPY','CHF','CAD','AUD','NZD'] },
  { name: 'Middle East',      emoji: '🕌', currencies: ['AED','SAR','QAR','KWD','BHD','OMR','JOD','LBP','SYP','IQD','IRR','YER','GEL','AMD','AZN','TRY'] },
  { name: 'Asia-Pacific',     emoji: '🌏', currencies: ['CNY','CNH','INR','KRW','SGD','HKD','MYR','IDR','PHP','THB','VND','PKR','BDT','LKR','NPR','MMK','KHR','LAK','BND','MOP','TWD','NTD','MNT','KZT','UZS','TJS','KGS','TMT','AFN','BTN','MVR','KPW'] },
  { name: 'Europe',           emoji: '🏰', currencies: ['NOK','SEK','DKK','ISK','PLN','CZK','HUF','RON','BGN','HRK','RSD','BAM','MKD','ALL','RUB','UAH','BYN','MDL'] },
  { name: 'Africa',           emoji: '🌍', currencies: ['ZAR','EGP','NGN','KES','TZS','UGX','GHS','ETB','MAD','TND','DZD','LYD','SDG','AOA','BWP','MZN','ZMW','ZWL','NAD','SZL','LSL','MWK','RWF','BIF','DJF','SOS','ERN','MRU','SLL','LRD','GMD','GNF','CVE','XOF','XAF','KMF','MUR','SCR','MGA','CDF'] },
  { name: 'North America',    emoji: '🗽', currencies: ['MXN','GTQ','HNL','NIO','CRC','PAB','BZD','SVC'] },
  { name: 'Caribbean',        emoji: '🌴', currencies: ['JMD','TTD','BSD','BBD','XCD','HTG','DOP','CUP','KYD','BMD','AWG'] },
  { name: 'South America',    emoji: '🌎', currencies: ['BRL','ARS','CLP','COP','PEN','VES','UYU','PYG','BOB','GYD','SRD'] },
  { name: 'Oceania',          emoji: '🏝️', currencies: ['FJD','PGK','SBD','VUV','WST','TOP','XPF'] },
];

export default function CountrySidebar({ countryInfo, activeCurrency }) {
  const navigate = useNavigate();
  const [openRegions, setOpenRegions] = useState({ 'Middle East': true, 'Major Currencies': true });
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileOpen, setMobileOpen]   = useState(false);
  const listRef  = useRef(null);
  const inputRef = useRef(null); // keep input focused across re-renders

  const toggleRegion = useCallback((name) => {
    setOpenRegions(prev => ({ ...prev, [name]: !prev[name] }));
  }, []);

  const handleNavigate = useCallback((country, currency) => {
    navigate(`/${buildSlug(country, currency)}`);
    setMobileOpen(false);
  }, [navigate]);

  // Scroll active currency into view in the list
  useEffect(() => {
    if (activeCurrency && listRef.current) {
      const el = listRef.current.querySelector(`[data-currency="${activeCurrency}"]`);
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [activeCurrency]);

  const filtered = searchQuery.trim()
    ? REGIONS
        .map(r => ({
          ...r,
          currencies: r.currencies.filter(c => {
            const info = countryInfo[c];
            if (!info) return false;
            const q = searchQuery.toLowerCase();
            return info.country.toLowerCase().includes(q) || c.toLowerCase().includes(q);
          }),
        }))
        .filter(r => r.currencies.length > 0)
    : REGIONS;

  // ── The actual sidebar JSX — inlined here, NOT a nested component ──────────
  // This is the key fix: defining SidebarContent as a nested component inside
  // render caused React to unmount/remount it (and the input) on every keystroke.
  const sidebarJSX = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-amber-100 bg-gradient-to-r from-yellow-600 to-amber-600">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-yellow-100" />
            <span className="text-sm font-bold text-white tracking-wide uppercase">All Countries</span>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden text-yellow-100 hover:text-white p-1 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {/* Search input — ref keeps focus stable */}
        <input
          ref={inputRef}
          type="text"
          placeholder="Search country or currency…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          autoComplete="off"
          className="w-full text-xs px-3 py-2 rounded-lg bg-white/20 text-white placeholder-yellow-200 border border-white/30 focus:outline-none focus:border-white focus:bg-white/30 transition-all"
        />
      </div>

      {/* Country list */}
      <div ref={listRef} className="flex-1 overflow-y-auto py-2 scrollbar-thin">
        {filtered.map(region => {
          const isOpen = searchQuery.trim() ? true : !!openRegions[region.name];
          return (
            <div key={region.name} className="mb-1">
              <button
                onClick={() => toggleRegion(region.name)}
                className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-amber-50 transition-colors group"
              >
                <div className="flex items-center gap-2">
                  <span className="text-base">{region.emoji}</span>
                  <span className="text-xs font-bold text-gray-700 uppercase tracking-wider group-hover:text-yellow-700 transition-colors">
                    {region.name}
                  </span>
                  <span className="text-xs text-gray-400 font-normal">({region.currencies.length})</span>
                </div>
                {isOpen
                  ? <ChevronDown className="w-3 h-3 text-gray-400" />
                  : <ChevronRight className="w-3 h-3 text-gray-400" />
                }
              </button>

              {isOpen && (
                <div className="pb-1">
                  {region.currencies.map(currency => {
                    const info = countryInfo[currency];
                    if (!info) return null;
                    const isActive = activeCurrency === currency;
                    return (
                      <button
                        key={currency}
                        data-currency={currency}
                        onClick={() => handleNavigate(info.country, currency)}
                        className={`w-full flex items-center gap-2 px-5 py-1.5 text-left transition-all hover:bg-amber-50 group ${
                          isActive ? 'bg-amber-100 border-r-2 border-yellow-500' : ''
                        }`}
                      >
                        <span className="text-base leading-none">{info.flag}</span>
                        <div className="flex-1 min-w-0">
                          <span className={`text-xs truncate block ${
                            isActive ? 'text-yellow-700 font-semibold' : 'text-gray-600 group-hover:text-gray-900'
                          }`}>
                            {info.country}
                          </span>
                        </div>
                        <span className={`text-xs font-mono shrink-0 ${
                          isActive ? 'text-yellow-600 font-bold' : 'text-gray-400'
                        }`}>
                          {currency}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-xs">
            No countries found for &ldquo;{searchQuery}&rdquo;
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-amber-100 bg-amber-50">
        <p className="text-xs text-gray-500 text-center">
          {Object.keys(countryInfo).length} currencies · {REGIONS.length} regions
        </p>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile FAB */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed bottom-6 right-6 z-50 bg-yellow-600 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-2xl hover:bg-yellow-700 transition-colors"
        title="Browse all countries"
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div className={`lg:hidden fixed top-0 right-0 h-full w-72 z-50 bg-white shadow-2xl transition-transform duration-300 ${
        mobileOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {sidebarJSX}
      </div>

      {/* Desktop sticky sidebar */}
      <div className="hidden lg:flex flex-col w-64 shrink-0 self-start sticky top-[72px] h-[calc(100vh-72px)] bg-white rounded-xl shadow-lg overflow-hidden border border-amber-100">
        {sidebarJSX}
      </div>
    </>
  );
}
